import { createContext, useCallback, useContext, useState } from 'react';
import { t } from 'i18next';
import { PaymentInput, Site } from '../../generated/graphql';
import { SessionInformation } from '../hooks/useStepHandler';
import { useHistory } from 'react-router-dom';
import { useBrowserData } from '../hooks/useBrowserData';
import {
  GooglePayPaymentMethod,
  GooglePayPaymentResponse,
} from '../types/google-pay';

type GooglePayContextType = {
  handleGooglePayClick: (
    amount: number,
    site: Site | null,
    sessionInformation: SessionInformation,
    setError: (message: string) => void,
    setGooglePayLoading: (loading: boolean) => void,
  ) => (data: google.payments.api.PaymentData) => void;
  allowedPaymentMethods: Array<GooglePayPaymentMethod>;
  getPaymentDataRequest: (
    amount: number,
  ) => google.payments.api.PaymentDataRequest;
};

const allowedCardAuthMethods =
  (process.env.REACT_APP_GOOGLE_PAY_ALLOWED_CARD_AUTH_METHODS?.split(',') || [
    'PAN_ONLY',
    'CRYPTOGRAM_3DS',
  ]) as Array<'PAN_ONLY' | 'CRYPTOGRAM_3DS'>;

export const GooglePayContext = createContext<GooglePayContextType>({
  handleGooglePayClick: () => () => {},
  allowedPaymentMethods: [],
  getPaymentDataRequest: () => ({}) as google.payments.api.PaymentDataRequest,
});

export const GooglePayProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const history = useHistory();
  const browserData = useBrowserData();

  const [allowedPaymentMethods, setAllowedPaymentMethods] = useState<
    Array<GooglePayPaymentMethod>
  >([
    {
      type: 'CARD',
      parameters: {
        allowedAuthMethods: allowedCardAuthMethods,
        allowedCardNetworks: ['AMEX', 'MASTERCARD', 'VISA'],
      },
      tokenizationSpecification: {
        type: 'PAYMENT_GATEWAY',
        parameters: {
          gateway: process.env.REACT_APP_GOOGLE_PAY_GATEWAY || '',
          gatewayMerchantId:
            process.env.REACT_APP_GOOGLE_PAY_GATEWAY_MERCHANT_ID || '',
        },
      },
    },
  ]);

  const getPaymentDataRequest = useCallback(
    (amount: number): google.payments.api.PaymentDataRequest => {
      return {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: allowedPaymentMethods,
        merchantInfo: {
          merchantId: process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID || '',
          merchantName: process.env.REACT_APP_GOOGLE_PAY_MERCHANT_NAME || '',
        },
        transactionInfo: {
          totalPriceStatus: 'FINAL',
          totalPriceLabel: 'Total',
          totalPrice: amount.toFixed(2),
          currencyCode: process.env.REACT_APP_GOOGLE_PAY_CURRENCY_CODE || '',
          countryCode: process.env.REACT_APP_GOOGLE_PAY_COUNTRY_CODE || '',
        },
      };
    },
    [allowedPaymentMethods],
  );

  const handleGooglePayClick = useCallback(
    (
      amount: number,
      site: Site | null,
      sessionInformation: SessionInformation,
      setError: (message: string) => void,
      setGooglePayLoading: (loading: boolean) => void,
    ) =>
      async (data: google.payments.api.PaymentData) => {
        setGooglePayLoading(true);
        const paymentInput: PaymentInput = {
          feeType: sessionInformation.selectedFee,
          storePayment: sessionInformation.checkedCreditCard,
          vrm: sessionInformation.vrm,
          duration: sessionInformation.duration,
          name: sessionInformation.name,
          reachedMaxDuration: sessionInformation.reachedMaxDuration,
          receiveEmails: sessionInformation.receiveEmails,
          receiveSms: sessionInformation.receiveSms,
          email: sessionInformation.email,
          phone: sessionInformation.phone,
          specialTariffPricingId: sessionInformation.specialTariffSelected,
          startTime: sessionInformation.startTime,
          timelineStepAccumulatedCost:
            sessionInformation.timelineStepAccumulatedCost,
          timelineStepDurationMinutes:
            sessionInformation.timelineStepDurationMinutes,
          timelineStepSpecialTariffId:
            sessionInformation.timelineStepSpecialTariffId,
          qrCode: site?.qrCode ?? '',
          topupFixedTime: sessionInformation.topupFixedTime,
          parkingTicketSessionId: sessionInformation.parkingTicketSessionId,
        };
        const response = await fetch(
          (process.env.REACT_APP_PUBLIC_URL || '').replace('/graphql', '') +
            '/api/google-pay/payment',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              amount,
              paymentData: data.paymentMethodData.tokenizationData.token,
              paymentInput,
              browserData,
            }),
          },
        );
        const responseData: GooglePayPaymentResponse = await response.json();
        setGooglePayLoading(false);
        if (responseData.error) {
          setError(t('paymentProcessingError'));
          history.push(`/failed?${responseData.error.message}`);
          return;
        }
        if (responseData.redirectUrl) {
          window.location.href = responseData.redirectUrl;
          return;
        }
        if (responseData.success) {
          history.push(`/confirmation/${responseData.data.particular}`);
          return;
        }
      },
    [browserData],
  );

  return (
    <GooglePayContext.Provider
      value={{
        handleGooglePayClick,
        allowedPaymentMethods,
        getPaymentDataRequest,
      }}
    >
      {children}
    </GooglePayContext.Provider>
  );
};

export const useGooglePay = () => {
  return useContext(GooglePayContext);
};
